import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  PictureAsPdf,
  Description,
  InsertDriveFile,
  Image,
  Article,
} from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description"; // For CSV
import { getImageUrl } from "../../utils/common";

function DocFiles({ url }) {
  // Function to extract the original filename from Cloudinary URL
  const getFileName = (fileUrl) => {
    try {
      let decodedUrl = decodeURIComponent(fileUrl);
      const parts = decodedUrl.split("/");
      const filenameWithParams = parts[parts.length - 1];
      const filename = filenameWithParams.split("?")[0]; // Remove query parameters

      // Remove numeric timestamp prefix (format: 123456789_filename.ext)
      const withoutTimestamp = filename.replace(/^\d+_/, "");

      return withoutTimestamp;
    } catch (error) {
      console.error("Error parsing filename:", error);
      return "document.pdf";
    }
  };

  const getFileExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const getFileIcon = (filename) => {
    const extension = getFileExtension(filename);

    switch (extension) {
      case "pdf":
        return <PictureAsPdf sx={{ color: "red", fontSize: 40 }} />;
      case "doc":
      case "docx":
        return <Article sx={{ color: "blue", fontSize: 40 }} />;
      case "xls":
      case "xlsx":
        return <DescriptionIcon sx={{ color: "green", fontSize: 40 }} />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "webp":
      case "svg":
        return <Image sx={{ color: "orange", fontSize: 40 }} />;
      default:
        return <Description sx={{ color: "gray", fontSize: 40 }} />;
    }
  };

  const extractUploadDate = (filename) => {
    try {
      // First try splitting by underscore, then by comma as fallback
      let parts = filename.split("_");
      if (parts.length < 2) {
        parts = filename.split(",");
      }

      // Get second-to-last part (now works with both _ and , separators)
      const timestampPart = parts.length >= 2 ? parts[parts.length - 2] : null;

      if (timestampPart && /^\d{8}$/.test(timestampPart)) {
        const year = timestampPart.substring(4, 8);
        const month = timestampPart.substring(2, 4);
        const day = timestampPart.substring(0, 2);
        return `${day}/${month}/${year}`;
      }
    } catch (error) {
      console.error("Error parsing upload date:", error);
    }
    return "N/A";
  };

  const handleDownload = () => {
    let newUrl = getImageUrl(url);
    const filename = getFileName(newUrl);
    const fileExtension = getFileExtension(filename);
    const isImageFile = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(fileExtension);

    // For images, use blob-based download to ensure proper file download
    if (isImageFile) {
      fetch(newUrl)
        .then(response => response.blob())
        .then(blob => {
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        })
        .catch(error => {
          console.error("Error downloading image:", error);
          // Fallback to direct link
          const link = document.createElement("a");
          link.href = newUrl;
          link.setAttribute("download", filename);
          link.setAttribute("target", "_blank");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    } else {
      // For non-image files (PDF, Excel, etc.), use the existing logic
      if (url.includes("res.cloudinary.com")) {
        // Clean up the URL by:
        // 1. Removing any existing fl_attachment parameter
        // 2. Fixing double extensions
        let cleanUrl = url
          .replace(/fl_attachment[^/]*\//, "")
          .replace(/(\.pdf)+\.pdf$/, ".pdf");

        // Construct the proper download URL
        const downloadUrl = cleanUrl;
        // Create and trigger download link
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const fileName = newUrl.substring(newUrl.lastIndexOf("/") + 1);
        const link = document.createElement("a");
        link.href = newUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };
  // ... (rest of the component remains the same)

  let fileName = getFileName(url);
  let parts = fileName.split("_");
  console.log("Original parts:", parts, fileName);

  // Remove last two elements if array has at least 2 elements
  if (parts.length >= 2) {
    parts = parts.slice(0, -2); // This removes the last two elements
  }

  const originalFileName = parts.join("_");

  const fileExtension = getFileExtension(fileName);
  const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(fileExtension);

  const uploadDate = extractUploadDate(decodeURIComponent(fileName.split("_")));

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        width: "100%",
        maxWidth: "300px",
        alignItems: "center",
        p: 1,
        border: "1px solid #eee",
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 60,
          height: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
          borderRadius: 1,
        }}
      >
        {isImage ? (
          <img
            src={getImageUrl(url)}
            alt={fileName}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        ) : (
          getFileIcon(fileName)
        )}
        <IconButton
          onClick={handleDownload}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            color: "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            width: 20,
            height: 20,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.7)",
            },
          }}
        >
          <ArrowDownwardIcon sx={{ width: 14, height: 14 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#001632",
          }}
        >
          {originalFileName}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: "#585858",
            mt: 0.5,
          }}
        >
          Uploaded on {uploadDate}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: "#585858",
            textTransform: "uppercase",
          }}
        >
          {fileExtension}
        </Typography>
      </Box>
    </Box>
  );
}

export default DocFiles;
